import Image from "next/image";
import Link from "next/link";
import React from "react";
import Logo from "../../assets/images/gameson-logo.webp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faFacebook,
  faInstagram,
  faTiktok,
  faXTwitter,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faPhone,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { GUIDE_GAMES } from "../../utils/guideSeo";

const SOCIAL = [
  { icon: faFacebook,  href: "https://www.facebook.com/GamesOnOfficial",   label: "Facebook",  color: "hover:bg-blue-600" },
  { icon: faInstagram, href: "https://www.instagram.com/g.games.on/",       label: "Instagram", color: "hover:bg-[#D62976]" },
  { icon: faXTwitter,  href: "https://twitter.com/GamesOnOfficial",         label: "X",         color: "hover:bg-black" },
  { icon: faYoutube,   href: "https://www.youtube.com/@Games-On-TH",        label: "YouTube",   color: "hover:bg-red-600" },
  { icon: faTiktok,    href: "https://www.tiktok.com/@gamesonofficial",     label: "TikTok",    color: "hover:bg-[#010101]" },
];

const CONTENT_LINKS = [
  { label: "ข่าวเกม",       href: "/news" },
  { label: "รีวิวเกม",      href: "/review" },
  { label: "คู่มือเกม",     href: "/guide" },
  { label: "บทความพิเศษ",   href: "/feature" },
  { label: "เกมทั้งหมด",    href: "/calendar" },
];

// Per-game guide hubs. These sit several clicks deep behind a game page, so
// without a site-wide link the whole guide tree below them is hard to reach —
// both for readers and for crawlers spreading link equity.
const GUIDE_HUBS = Object.entries(GUIDE_GAMES).map(([key, game]) => ({
  label: `ไกด์ ${game.abbrev}`,
  href: `/game/guide/${key}`,
}));

const INFO_LINKS = [
  { label: "Terms & Privacy", href: "/terms" },
  { label: "Contact Us",      href: "/contactus" },
];

const Footer = () => (
  <footer className="bg-stage-deep text-white">

    {/* ── Main grid ── */}
    <div className="container mx-auto xl:px-24 lg:px-12 md:px-8 px-4 pt-12 pb-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Col 1 — Brand */}
        <div className="flex flex-col gap-5 sm:col-span-2 lg:col-span-1">
          {/* 388x55 source → 140x20; a 22px height here made the declared box
              taller than the rendered one, which Lighthouse then blamed for the
              page's layout shifts. */}
          <Image
            src={Logo}
            width={140}
            height={20}
            alt="Game's On"
            className="object-contain"
          />
          <p className="text-[15px] text-white/70 leading-relaxed max-w-xs">
            เว็บไซต์บทความรีวิวเกม ข่าวเกม และคู่มือเกม
            สำหรับคนรักเกมทุกแพลตฟอร์ม
          </p>

          {/* Social icons */}
          <div className="flex items-center gap-2 flex-wrap">
            {SOCIAL.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className={`w-10 h-10 flex items-center justify-center text-white/75 hover:text-white bg-white/8 transition-colors duration-150 ${s.color}`}
              >
                <FontAwesomeIcon icon={s.icon as IconProp} className="text-base" />
              </a>
            ))}
          </div>
        </div>

        {/* Col 2 — Content */}
        <div className="flex flex-col gap-4">
          <h2 className="font-display text-lg font-extrabold italic text-white">เนื้อหา</h2>
          <ul className="grid grid-cols-2 sm:grid-cols-1 gap-x-4 gap-y-2.5">
            {[...CONTENT_LINKS, ...GUIDE_HUBS].map((l) => (
              <li key={l.href}>
                <Link
                  href={l.href}
                  prefetch={false}
                  className="text-[15px] text-white/70 hover:text-white transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 3 — Info */}
        <div className="flex flex-col gap-4">
          <h2 className="font-display text-lg font-extrabold italic text-white">ข้อมูล</h2>
          <ul className="flex flex-col gap-2.5">
            {INFO_LINKS.map((l) => (
              <li key={l.href}>
                <Link
                  href={l.href}
                  className="text-[15px] text-white/70 hover:text-white transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Col 4 — Contact */}
        <div className="flex flex-col gap-4">
          <h2 className="font-display text-lg font-extrabold italic text-white">ติดต่อเรา</h2>
          <ul className="flex flex-col gap-3">
            <li>
              <a
                href="mailto:contact@games-on.net"
                className="flex items-start gap-3 text-[15px] text-white/70 hover:text-white transition-colors"
              >
                <FontAwesomeIcon icon={faEnvelope} className="mt-1 text-white/50 w-4 flex-shrink-0" />
                contact@games-on.net
              </a>
            </li>
            <li>
              <a
                href="tel:024740332"
                className="flex items-center gap-3 text-[15px] text-white/70 hover:text-white transition-colors tabular-nums"
              >
                <FontAwesomeIcon icon={faPhone} className="text-white/50 w-4 flex-shrink-0" />
                02-474-0332
              </a>
            </li>
            <li className="flex items-start gap-3 text-[15px] text-white/70">
              <FontAwesomeIcon icon={faLocationDot} className="mt-1 text-white/50 w-4 flex-shrink-0" />
              <span className="leading-relaxed">
                155/59 Areeya Como Primo,<br />
                Bang Phi, Samut Prakan 10540
              </span>
            </li>
          </ul>
        </div>

      </div>
    </div>

    {/* ── Bottom bar ── */}
    <div className="bg-black/30">
      <div className="container mx-auto xl:px-24 lg:px-12 md:px-8 px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
        <p className="text-[13px] text-white/55 text-center sm:text-left">
          © {new Date().getFullYear()} CRUSHKICK CO., LTD. All rights reserved.
        </p>
        <div className="flex items-center gap-4">
          <Link href="/terms" className="text-[13px] text-white/55 hover:text-white transition-colors">
            Privacy Policy
          </Link>
          <span aria-hidden="true" className="text-white/30">·</span>
          <Link href="/contactus" className="text-[13px] text-white/55 hover:text-white transition-colors">
            Contact
          </Link>
        </div>
      </div>
    </div>

  </footer>
);

export default Footer;
